/**
 * Polygon Validation Service
 * Validates drawn polygons against boundaries and existing polygons
 */
import { Injectable } from '@angular/core';
import {isSelfIntersecting} from '@app/utils/polygons.utils';

@Injectable({
  providedIn: 'root'
})
export class PolygonValidationService {

  /**
   * Check if a point is inside a polygon using ray-casting algorithm
   */
  private isPointInPolygon(point: [number, number], polygon: [number, number][]): boolean {
    const [x, y] = point;
    let inside = false;
    const epsilon = 1e-7;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0];
      const yi = polygon[i][1];
      const xj = polygon[j][0];
      const yj = polygon[j][1];

      // Treat points lying on polygon edges as inside.
      const cross = (x - xi) * (yj - yi) - (y - yi) * (xj - xi);
      if (Math.abs(cross) <= epsilon) {
        const minX = Math.min(xi, xj) - epsilon;
        const maxX = Math.max(xi, xj) + epsilon;
        const minY = Math.min(yi, yj) - epsilon;
        const maxY = Math.max(yi, yj) + epsilon;
        if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
          return true;
        }
      }

      const intersect = ((yi > y) !== (yj > y)) &&
        (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

      if (intersect) inside = !inside;
    }

    return inside;
  }

  /**
   * Check if a polygon is entirely within country boundaries
   * @param drawnPolygon - The polygon being drawn
   * @param countryPolygons - Array of region polygons forming the country
   */
  isPolygonWithinCountry(
    drawnPolygon: [number, number][],
    countryPolygons: Array<{ coordinates: [number, number][] }>
  ): boolean {
    if (!drawnPolygon || drawnPolygon.length < 3) {
      return false;
    }

    if (!countryPolygons || countryPolygons.length === 0) {
      return true; // If no boundaries defined, allow drawing
    }

    // Check if ALL points of the drawn polygon are inside at least one region
    for (const point of drawnPolygon) {
      let isPointInAnyRegion = false;

      for (const regionPolygon of countryPolygons) {
        if (this.isPointInPolygon(point, regionPolygon.coordinates)) {
          isPointInAnyRegion = true;
          break;
        }
      }

      // If any point is outside all regions, the polygon is invalid
      if (!isPointInAnyRegion) {
        return false;
      }
    }

    return true;
  }

  /**
   * Check if a polygon crosses region boundaries
   * @param drawnPolygon - The polygon being drawn
   * @param regionPolygons - Array of region polygons
   */
  isPolygonCrossingBoundaries(
    drawnPolygon: [number, number][],
    regionPolygons: Array<{ id: string | number; coordinates: [number, number][] }>
  ): boolean {
    if (!drawnPolygon || drawnPolygon.length < 3) {
      return false;
    }

    if (!regionPolygons || regionPolygons.length === 0) {
      return false;
    }

    // Find which region(s) contain the polygon points
    const regionsContainingPoints = new Set<string | number>();

    for (const point of drawnPolygon) {
      for (const region of regionPolygons) {
        if (this.isPointInPolygon(point, region.coordinates)) {
          regionsContainingPoints.add(region.id);
        }
      }
    }

    // If points are in more than one region, it crosses boundaries
    return regionsContainingPoints.size > 1;
  }

  /**
   * Check if drawn polygon overlaps with existing monitoring polygons
   * @param drawnPolygon - The polygon being drawn
   * @param existingPolygons - Array of existing monitoring polygons
   */
  isPolygonOverlapping(
    drawnPolygon: [number, number][],
    existingPolygons: Array<{ coordinates: [number, number][] }>
  ): boolean {
    if (!drawnPolygon || drawnPolygon.length < 3) {
      return false;
    }

    if (!existingPolygons || existingPolygons.length === 0) {
      return false;
    }

    // Check if any point of drawn polygon is inside existing polygons
    for (const point of drawnPolygon) {
      for (const existingPolygon of existingPolygons) {
        if (this.isPointInPolygon(point, existingPolygon.coordinates)) {
          return true; // Overlap detected
        }
      }
    }

    // Also check if any point of existing polygons is inside drawn polygon
    for (const existingPolygon of existingPolygons) {
      for (const point of existingPolygon.coordinates) {
        if (this.isPointInPolygon(point, drawnPolygon)) {
          return true; // Overlap detected
        }
      }
    }

    // Check for edge intersections using Sutherland-Hodgman algorithm concept
    for (const existingPolygon of existingPolygons) {
      if (this.doPolygonsIntersect(drawnPolygon, existingPolygon.coordinates)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if two line segments intersect
   */
  private doLineSegmentsIntersect(
    p1: [number, number],
    p2: [number, number],
    p3: [number, number],
    p4: [number, number]
  ): boolean {
    const denominator = ((p4[1] - p3[1]) * (p2[0] - p1[0])) - ((p4[0] - p3[0]) * (p2[1] - p1[1]));

    if (denominator === 0) {
      return false; // Lines are parallel
    }

    const ua = (((p4[0] - p3[0]) * (p1[1] - p3[1])) - ((p4[1] - p3[1]) * (p1[0] - p3[0]))) / denominator;
    const ub = (((p2[0] - p1[0]) * (p1[1] - p3[1])) - ((p2[1] - p1[1]) * (p1[0] - p3[0]))) / denominator;

    return (ua >= 0 && ua <= 1) && (ub >= 0 && ub <= 1);
  }

  /**
   * Check if two polygons intersect (edges cross)
   */
  private doPolygonsIntersect(polygon1: [number, number][], polygon2: [number, number][]): boolean {
    // Check if any edges of polygon1 intersect with any edges of polygon2
    for (let i = 0; i < polygon1.length; i++) {
      const p1 = polygon1[i];
      const p2 = polygon1[(i + 1) % polygon1.length];

      for (let j = 0; j < polygon2.length; j++) {
        const p3 = polygon2[j];
        const p4 = polygon2[(j + 1) % polygon2.length];

        if (this.doLineSegmentsIntersect(p1, p2, p3, p4)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Validate a drawn polygon against all constraints
   * @returns object with validation result and error message
   */
  validatePolygon(
    drawnPolygon: [number, number][],
    countryPolygons: Array<{ id: string | number; coordinates: [number, number][] }>,
    existingPolygons: Array<{ coordinates: [number, number][] }>
  ): { isValid: boolean; error?: string } {

    // Check if polygon has self-intersections
    if (isSelfIntersecting(drawnPolygon)) {
      return {
        isValid: false,
        error: 'ERROR_POLYGON_SELF_INTERSECTING'
      };
    }

    // Check if polygon is within country boundaries
    if (!this.isPolygonWithinCountry(drawnPolygon, countryPolygons)) {
      return {
        isValid: false,
        error: 'POLYGON_OUTSIDE_COUNTRY_BOUNDARIES'
      };
    }

    // NOTE: Commented out - Allow polygons to cross region boundaries
    // This is a valid use case when monitoring areas span multiple regions
    // if (this.isPolygonCrossingBoundaries(drawnPolygon, countryPolygons)) {
    //   return {
    //     isValid: false,
    //     error: 'POLYGON_CROSSES_REGION_BOUNDARIES'
    //   };
    // }

    // Check if polygon overlaps with existing monitoring polygons
    if (this.isPolygonOverlapping(drawnPolygon, existingPolygons)) {
      return {
        isValid: false,
        error: 'POLYGON_OVERLAPS_EXISTING'
      };
    }

    return { isValid: true };
  }


  /**
   * Get the bounding box of a polygon
   */

  /**
   * Check if a single point is inside any of the provided country polygons
   */
  public isPointWithinCountry(
    point: [number, number],
    countryPolygons: Array<{ coordinates: [number, number][] }>
  ): boolean {
    if (!point) return false;
    if (!countryPolygons || countryPolygons.length === 0) return true;

    for (const regionPolygon of countryPolygons) {
      if (this.isPointInPolygon(point, regionPolygon.coordinates)) {
        return true;
      }
    }

    return false;
  }
}
